import { useState, type ReactNode } from 'react';
import { CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';
import { ApiError } from '@/api/core/client';
import { createPoFromDraft, type CreatedPo, type DraftDetail } from '@/api/orderPrep';
import { Disclosure } from '@/components/Disclosure';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { statusInk } from '@/lib/status';
import { cn } from '@/lib/utils';
import { acotPoUrl } from './lib';

function PoLink({ poId, className }: { poId: number; className?: string }) {
  return (
    <a href={acotPoUrl(poId)} target="_blank" rel="noreferrer" className={cn('inline-flex items-center gap-1 underline underline-offset-2', className)}>
      PO #{poId} <ExternalLink className="h-3 w-3" />
    </a>
  );
}

const lineCount = (n: number) => `${n} line${n === 1 ? '' : 's'}`;

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{children}</dd>
    </div>
  );
}

/**
 * Create PO on the Draft view: confirm the facts, submit the lines as they
 * stand, show the PO number and its link. The journey's status does not move;
 * Mark ordered stays its own step.
 */
export function CreatePoAction({ detail, supplierId, poId, onChanged }: {
  detail: DraftDetail;
  supplierId: number | null;
  poId: number | null;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedPo | null>(null);
  // Held locally so the link shows at once, ahead of the journey's refetch.
  const [lastPoId, setLastPoId] = useState<number | null>(null);

  if (detail.status === 'closed') return null;
  if (supplierId == null) {
    return <span className={cn('ml-auto', statusInk('warning'))}>No supplier for a PO</span>;
  }

  const existingPo = lastPoId ?? poId;
  const plan = detail.poPlan;
  const noProductUnits = plan.noProduct.reduce((sum, line) => sum + line.qty, 0);
  const verb = existingPo != null ? 'Create another PO' : 'Create PO';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await createPoFromDraft(detail.orderId, {
        ifVersion: detail.version,
        another: existingPo != null,
      });
      setCreated(result);
      setLastPoId(result.poId);
      await onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && (e.details as { error?: string } | null)?.error === 'stale_version') {
        await onChanged();
        setError('The draft just changed. These are the current numbers.');
      } else {
        setError(e instanceof Error ? e.message : 'Could not create the PO');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="ml-auto inline-flex items-center gap-2">
      {existingPo != null && <PoLink poId={existingPo} className="text-foreground" />}
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        onClick={() => { setCreated(null); setError(null); setOpen(true); }}
      >
        {verb}
      </Button>
      <Dialog open={open} onOpenChange={(next) => { if (!busy) setOpen(next); }}>
        <DialogContent aria-describedby={undefined} className="text-sm sm:max-w-xl">
          {created ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className={cn('h-5 w-5', statusInk('success'))} />
                  PO #{created.poId} created
                </DialogTitle>
              </DialogHeader>
              <dl className="space-y-1">
                <Fact label="Products">{created.products}</Fact>
                <Fact label="Units">{created.units}</Fact>
                <Fact label="Journey">not marked ordered</Fact>
              </dl>
              {!created.recorded && (
                <p className={statusInk('warning')}>Not saved on the journey. Note this PO number.</p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
                <Button asChild>
                  <a href={acotPoUrl(created.poId)} target="_blank" rel="noreferrer">
                    Open PO #{created.poId} <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{verb}</DialogTitle>
              </DialogHeader>
              <dl className="space-y-1">
                <Fact label="Supplier">{detail.journey?.vendor ?? 'Supplier'} #{supplierId}</Fact>
                <Fact label="Products">{plan.products}</Fact>
                <Fact label="Units">{plan.units}</Fact>
              </dl>
              {plan.items.length > 0 && (
                <div className="max-h-[40dvh] overflow-y-auto rounded-md border border-border/60">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted text-muted-foreground">
                      <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-medium">
                        <th>Product</th>
                        <th className="w-28">Item #</th>
                        <th className="w-14 text-right!">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.items.map((item) => (
                        <tr key={item.pid} className="border-t border-border/40 [&>td]:px-2 [&>td]:py-1">
                          <td className="break-words">{item.description}</td>
                          <td className="break-all text-muted-foreground">{item.itemnumber ?? '—'}</td>
                          <td className="text-right font-medium tabular-nums">{item.qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {existingPo != null && (
                <p className={statusInk('warning')}>
                  This journey already has <PoLink poId={existingPo} />.
                </p>
              )}
              {plan.noProduct.length > 0 && (
                <div>
                  <p className={statusInk('warning')}>
                    Left off: {lineCount(plan.noProduct.length)}, {noProductUnits} units with no product yet
                  </p>
                  <Disclosure label="Lines">
                    <ul className="max-h-40 space-y-0.5 overflow-y-auto text-xs text-muted-foreground">
                      {plan.noProduct.map((line, i) => (
                        <li key={i} className="flex justify-between gap-3">
                          <span className="min-w-0 truncate">{line.description}</span>
                          <span className="tabular-nums">{line.qty}</span>
                        </li>
                      ))}
                    </ul>
                  </Disclosure>
                </div>
              )}
              {plan.unsized > 0 && (
                <p className={statusInk('warning')}>Left off: {lineCount(plan.unsized)} not sized yet</p>
              )}
              {plan.products === 0 && (
                <p className={statusInk('warning')}>No line has both a product and a quantity.</p>
              )}
              {error && <p className={statusInk('danger')}>{error}</p>}
              <DialogFooter>
                <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
                <Button disabled={busy || plan.products === 0} onClick={submit}>
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {verb}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </span>
  );
}
